import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import { Button } from "@nosible/shared/components/ui/button";
import { Alert, AlertDescription } from "@nosible/shared/components/ui/alert";
import { trpc } from "@/admin/lib/trpc";
import { formatDuration, formatTimeAgo, formatTimeUntil } from "@/admin/lib/utils";
import {
  RefreshCw,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertCircle,
  Key,
  Clock,
} from "lucide-react";

interface AuthSyncCardProps {
  hasPermission: boolean;
}

export function AuthSyncCard({ hasPermission }: AuthSyncCardProps) {
  const utils = trpc.useUtils();

  const { data: syncStatus } = trpc.admin.sync.authSync.getSyncStatus.useQuery(
    undefined,
    {
      enabled: hasPermission,
      refetchInterval: 5000,
    }
  );

  const triggerSync = trpc.admin.sync.authSync.triggerSync.useMutation({
    onSuccess: () => {
      utils.admin.sync.authSync.getSyncStatus.invalidate();
    },
  });

  const isInSync =
    syncStatus?.stats?.redisKeyCount === syncStatus?.stats?.dbActiveKeyCount;

  return (
    <Card className="bg-slate-core border-chromium-mist/20">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Auth Sync
            </CardTitle>
            <CardDescription>Postgres → Redis (API Keys & Subscriptions)</CardDescription>
          </div>
          <Button
            size="sm"
            onClick={() => triggerSync.mutate()}
            disabled={syncStatus?.syncInProgress || triggerSync.isPending}
            className="bg-neon-green hover:bg-neon-green/90 text-obsidian"
          >
            {syncStatus?.syncInProgress || triggerSync.isPending ? (
              <>
                <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-3 w-3" />
                Sync Now
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-chromium-mist">Last Sync</span>
            <div className="flex items-center gap-2">
              {syncStatus?.lastSyncStatus === "success" ? (
                <CheckCircle2 className="h-4 w-4 text-neon-green" />
              ) : syncStatus?.lastSyncStatus === "error" ? (
                <XCircle className="h-4 w-4 text-red-500" />
              ) : null}
              <span className="text-sm font-medium">
                {formatTimeAgo(syncStatus?.lastSyncTime || null)}
              </span>
            </div>
          </div>

          {syncStatus?.lastSyncDuration && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-chromium-mist">Duration</span>
              <span className="text-sm font-medium">
                {formatDuration(syncStatus.lastSyncDuration)}
              </span>
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="text-sm text-chromium-mist">Next Sync</span>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-chromium-mist" />
              <span className="text-sm font-medium">
                {formatTimeUntil(syncStatus?.nextSyncTime || null)}
              </span>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="text-sm font-medium text-ghost-white">Statistics</div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-chromium-mist">API Keys</span>
              {syncStatus?.stats?.redisKeyCount === syncStatus?.stats?.dbActiveKeyCount ? (
                <CheckCircle2 className="h-3 w-3 text-neon-green" />
              ) : (
                <AlertCircle className="h-3 w-3 text-yellow-500" />
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-obsidian/50 p-2 rounded text-center">
                <div className="text-chromium-mist">Redis</div>
                <div className="font-bold text-base">{syncStatus?.stats?.redisKeyCount || 0}</div>
              </div>
              <div className="bg-obsidian/50 p-2 rounded text-center">
                <div className="text-chromium-mist">DB</div>
                <div className="font-bold text-base">{syncStatus?.stats?.dbActiveKeyCount || 0}</div>
              </div>
            </div>
          </div>

        </div>

        {isInSync && (
          <div className="flex items-center gap-2 p-2 bg-neon-green/10 rounded-md border border-neon-green/20">
            <CheckCircle2 className="h-4 w-4 text-neon-green" />
            <span className="text-xs text-neon-green font-medium">In sync</span>
          </div>
        )}

        {syncStatus?.lastSyncError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs">
              {syncStatus.lastSyncError}
            </AlertDescription>
          </Alert>
        )}

        {triggerSync.isSuccess && (
          <Alert className="bg-neon-green/10 border-neon-green">
            <CheckCircle2 className="h-4 w-4 text-neon-green" />
            <AlertDescription className="text-xs text-neon-green">
              {triggerSync.data.message}
            </AlertDescription>
          </Alert>
        )}

        <div className="text-xs text-chromium-mist/70 pt-2 border-t border-chromium-mist/10">
          Scheduled every 5 min. Auth data is synced from Postgres to Redis.
        </div>
      </CardContent>
    </Card>
  );
}
